/**
 * Payment-success flags, readable across the payment popup boundary.
 *
 * sessionStorage is per tab, so a flag written from the gateway return page inside the popup is lost
 * when that popup closes (and `window.opener` is often severed by the PayU -> backend -> frontend
 * redirect chain, so it cannot be written into the opener either). localStorage is shared by every tab
 * of the origin, which is the only place the popup can reliably leave a note for the landing tab.
 *
 * Reads prefer sessionStorage (per-tab semantics when they do work) and fall back to localStorage,
 * clearing both so a flag can never fire twice.
 */

const LOCAL_PREFIX = 'checkout_flag:'

function safeGet(storage: Storage | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function safeRemove(storage: Storage | null, key: string): void {
  try {
    storage?.removeItem(key)
  } catch {
    /* */
  }
}

function safeSet(storage: Storage | null, key: string, value: string): void {
  try {
    storage?.setItem(key, value)
  } catch {
    /* */
  }
}

/** True when the flag is set in either storage; clears both. */
export function consumeCheckoutFlag(key: string): boolean {
  if (typeof window === 'undefined') return false
  const localKey = `${LOCAL_PREFIX}${key}`
  const inSession = safeGet(window.sessionStorage, key) === '1'
  const inLocal = safeGet(window.localStorage, localKey) === '1'
  if (!inSession && !inLocal) return false
  safeRemove(window.sessionStorage, key)
  safeRemove(window.localStorage, localKey)
  return true
}

/** Set the flag in this tab's sessionStorage and, for cross-tab delivery, in localStorage. */
export function armCheckoutFlag(key: string): void {
  if (typeof window === 'undefined') return
  safeSet(window.sessionStorage, key, '1')
  safeSet(window.localStorage, `${LOCAL_PREFIX}${key}`, '1')
}

/** Clear both storages for a key (e.g. after acknowledgement). */
export function clearCheckoutFlag(key: string): void {
  if (typeof window === 'undefined') return
  safeRemove(window.sessionStorage, key)
  safeRemove(window.localStorage, `${LOCAL_PREFIX}${key}`)
}

/**
 * Watch for a flag that arrives after the page mounted.
 *
 * The gateway return page and the landing tab are two windows racing each other: the landing tab often
 * mounts first, consumes nothing, and would otherwise never look again. A `storage` event fires in the
 * *other* tabs when localStorage changes, which is exactly the window we need.
 */
export function watchCheckoutFlag(key: string, onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const localKey = `${LOCAL_PREFIX}${key}`
  const handler = (event: StorageEvent) => {
    if (event.key === localKey || event.key === key) onChange()
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/**
 * Read without clearing. Use this when the page that reads the flag can still be remounted before it
 * renders (loading / session-restore gates): a consuming read would clear it and the confirmation would
 * be lost. Clear it explicitly when the modal is dismissed instead.
 */
export function hasCheckoutFlag(key: string): boolean {
  if (typeof window === 'undefined') return false
  return safeGet(window.sessionStorage, key) === '1' || safeGet(window.localStorage, `${LOCAL_PREFIX}${key}`) === '1'
}
